// import FullLoader from "components/loader/full-loader";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import React, { useState, useEffect } from "react";
import {  Dropdown } from "react-bootstrap";
import Swal from "sweetalert2";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import { formatPrice, formatTax } from "utils/utils";
import { useTransaction } from "./TransactionContext";
import { PaymentMethodProvider } from "contexts/v1/masterData/PaymentMethodContext";
import EditTransactionPage from "../components/EditTransactionPage";
import { MenuProvider } from "../Products/MenuContext";
import { deleteRequest } from "services/api";
import { CategoryProvider } from "../context/CategoryContext";
// import FilterCard from "components/FilterCard";
// import printJS from "print-js";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import { printReceipt } from "./ReceiptPrinter";
import DailySummaryCard from "./DailySummaryCard";
import SummaryTotals from "../components/SummaryTotals";
import FilterCard from "./FilterCard";
import * as XLSX from "xlsx";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { useDailyTransaction } from "../Report/DailyTransactionContext";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const POS_History = () => {
  // const [transactions, setTransactions] = useState(dummyTransactions);
  const {
    transactions,
    Pagination,
    refetchTransaction,
    filterOptions,
    updateFilter,
    clearFilters,
  } = useTransaction();

  const {
    summary,
    summaryData,
    setDateRange,
    refetchTransaction: refetchReport,
  } = useDailyTransaction();

  // Sync date filter with report context
  useEffect(() => {
    if (filterOptions.filters.inserted_at) {
      setDateRange(
        filterOptions.filters.inserted_at,
        filterOptions.filters.inserted_at
      );
    }
  }, [filterOptions.filters.inserted_at, setDateRange]);

  const [exportDataCount, setExportDataCount] = useState(0);

  const [expandedRow, setExpandedRow] = useState(null);

  // Function to get total count of filtered data
  const getFilteredDataCount = async () => {
    try {
      const queryString = buildQueryString({
        search: filterOptions.search || "",
        include: "paymentMethod,transactionItems,cashierSession.cashier,user",
        ...(filterOptions.filters.payment_method_id && {
          "filter[payment_method_id]": filterOptions.filters.payment_method_id,
        }),
        ...(filterOptions.filters.cashier_session_id && {
          "filter[cashier_session_id]":
            filterOptions.filters.cashier_session_id,
        }),
        ...(filterOptions.filters.location && {
          "filter[location]": filterOptions.filters.location,
        }),
        ...(filterOptions.filters.inserted_at && {
          "filter[inserted_at]": filterOptions.filters.inserted_at,
        }),
        paginate: 1, // Just get count, not data
      });

      const response = await getRequest(`/transaction${queryString}`);
      if (response.success) {
        setExportDataCount(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error getting data count:", error);
      setExportDataCount(0);
    }
  };

  // Update count when filters change
  useEffect(() => {
    getFilteredDataCount();
  }, [filterOptions]);

  const {
    handleSuccess,
    showCanvasHandler,
    handleSuccessToast,
  } = useCanvasHandler();

  const toggleExpand = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Apakah Anda yakin?",
      text: "Anda tidak akan dapat mengembalikan ini!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Ya, hapus!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Handle delete action
        const response = await deleteRequest(`/transaction/${id}`);

        if (response.success) {
          handleSuccessToast(true, "Data deleted successfully");
          refetchTransaction();
          refetchReport();
        }
      }
    });
  };

  const handleExportExcel = async () => {
    try {
      // Show loading
      Swal.fire({
        title: "Exporting...",
        text: "Mengambil semua data sesuai filter...",
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      // Build query string for all data (without pagination)
      const queryString = buildQueryString({
        search: filterOptions.search || "",
        include: "paymentMethod,transactionItems,cashierSession.cashier,user",
        // Apply all current filters
        ...(filterOptions.filters.payment_method_id && {
          "filter[payment_method_id]": filterOptions.filters.payment_method_id,
        }),
        ...(filterOptions.filters.cashier_session_id && {
          "filter[cashier_session_id]":
            filterOptions.filters.cashier_session_id,
        }),
        ...(filterOptions.filters.location && {
          "filter[location]": filterOptions.filters.location,
        }),
        ...(filterOptions.filters.inserted_at && {
          "filter[inserted_at]": filterOptions.filters.inserted_at,
        }),
        // Get all data without pagination
        paginate: 10000, // Large number to get all data
      });

      // Fetch all filtered data
      const response = await getRequest(`/transaction${queryString}`);

      if (!response.success) {
        throw new Error(response.message || "Failed to fetch data");
      }

      const allTransactions = response.data || [];

      if (allTransactions.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Data",
          text: "Tidak ada data transaksi untuk diekspor sesuai filter yang dipilih",
        });
        return;
      }

      // Close loading
      Swal.close();

      // Show success with data count
      Swal.fire({
        icon: "info",
        title: "Data Found",
        text: `Ditemukan ${allTransactions.length} data transaksi. Memproses export...`,
        timer: 2000,
        showConfirmButton: false,
      });

      // Prepare data for Excel
      const excelData = allTransactions.map((transaction, index) => ({
        No: index + 1,
        Code: transaction.code || "-",
        Date: new Date(transaction.created_at).toLocaleString(),
        Cashier: transaction.user?.name || "-",
        Location: transaction.location || "-",
        "Payment Method": transaction.payment_method?.name || "-",
        "Tax (%)": formatTax(transaction.tax),
        Subtotal: Number(transaction.total_price) || 0,
        "Tax Amount": (Number(transaction.total_price) * transaction.tax) / 100,
        "Grand Total": Number(transaction.grand_total) || 0,
        Notes: transaction.notes || "-",
        // Add transaction items as separate rows if needed
        Items:
          transaction.transaction_items
            ?.map(
              (item) =>
                `${item.item_name} (${item.quantity}x${formatPrice(
                  Number(item.price)
                )})`
            )
            .join("; ") || "-",
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Auto-size columns
      const colWidths = [
        { wch: 5 }, // No
        { wch: 15 }, // Code
        { wch: 20 }, // Date
        { wch: 15 }, // Cashier
        { wch: 12 }, // Location
        { wch: 15 }, // Payment Method
        { wch: 8 }, // Tax (%)
        { wch: 12 }, // Subtotal
        { wch: 12 }, // Tax Amount
        { wch: 12 }, // Grand Total
        { wch: 20 }, // Notes
        { wch: 50 }, // Items
      ];
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Transactions");

      // Generate filename with current date and filter info
      const currentDate = new Date().toISOString().split("T")[0];
      const filterDate = filterOptions.filters.inserted_at || currentDate;
      const filename = `transaction_history_${filterDate}_${allTransactions.length}_records.xlsx`;

      // Save file using Blob and anchor with download attribute for better Android compatibility
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      handleSuccessToast(
        true,
        `Berhasil export ${allTransactions.length} data ke Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Terjadi kesalahan saat mengekspor data: " + error.message,
      });
    }
  };

  //   if (!transactions) {
  //     return <FullLoader />;
  //   }

  // const printReceiptOLD = (transaction) => {
  //   const itemsTable = transaction.transaction_items
  //     .map(
  //       (item) => `
  //     <tr>
  //       <td>${item.item_name}<br/>${item.quantity} x Rp ${Number(
  //         item.price
  //       ).toLocaleString()}</td>
  //       <td style="text-align: right;">Rp ${(
  //         item.quantity * Number(item.price)
  //       ).toLocaleString()}</td>
  //     </tr>
  //   `
  //     )
  //     .join("");

  //   const subtotal = transaction.transaction_items.reduce(
  //     (sum, item) => sum + item.quantity * Number(item.price),
  //     0
  //   );
  //   // const tax = subtotal * 0.1;
  //   const tax = (subtotal * transaction.tax) / 100;
  //   const total = subtotal + tax;

  //   const printableHTML = `
  //     <div style="text-align: center; font-size: 16px; font-weight: bold;">
  //       <img src="/Kuber.jpeg" style="width: 120px; height: auto; margin-bottom: 5px;" /><br>
  //       ${transaction.code || "-"}<br/>
  //       ${new Date(transaction.created_at).toLocaleString()}
  //     </div>
  //     <hr/>
  //     <table style="width: 100%; font-size: 14px;">
  //       <thead>
  //         <tr><th style="text-align: left;">Item</th><th style="text-align: right;">Total</th></tr>
  //       </thead>
  //       <tbody>${itemsTable}</tbody>
  //     </table>
  //     <hr/>
  //     <table style="width: 100%; font-size: 14px;">
  //       <tr><td>Subtotal</td><td style="text-align: right;">Rp ${subtotal.toLocaleString()}</td></tr>
  //       <tr><td>Tax (${formatTax(
  //         transaction.tax
  //       )}%)</td><td style="text-align: right;">Rp ${tax.toLocaleString()}</td></tr>
  //       <tr><td><strong>Grand Total</strong></td><td style="text-align: right;"><strong>Rp ${total.toLocaleString()}</strong></td></tr>
  //       <tr><td>Payment Method</td><td style="text-align: right;">${
  //         transaction.payment_method?.name || "-"
  //       }</td></tr>
  //     </table>
  //     <hr/>
  //     <div style="text-align: center; font-size: 14px;">
  //       <strong>Thank you!</strong><br>Please keep this receipt as proof of your payment.
  //     </div>
  //   `;

  //   printJS({
  //     printable: printableHTML,
  //     type: "raw-html",
  //     style: `
  //       @page { size: auto; margin: 10mm; }
  //       body { font-family: Arial, sans-serif; font-size: 14px; }
  //       table, th, td { border: none; padding: 4px; }
  //     `,
  //   });
  // };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-5">
        {/* Filter Card */}
        <PaymentMethodProvider>
          <FilterCard
            filterOptions={filterOptions}
            updateFilter={updateFilter}
            clearFilters={clearFilters}
          />
        </PaymentMethodProvider>

        {/* Daily Summary Card - pass filters */}
        <DailySummaryCard filters={filterOptions.filters} />

        {/* Summary Totals */}
        <SummaryTotals summaryData={summaryData} containerClass="row row-cards my-3" />

        {/* Graph Summary */}
        {summary?.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">Graph Summary</h5>
              <p className="text-muted">
                Data yang tampil adalah{" "}
                {filterOptions.filters.inserted_at || "hari ini"}.
              </p>
              <div style={{ height: "300px" }}>
                <Bar
                  data={{
                    labels: summary.map((item) => item.menu),
                    datasets: [
                      {
                        label: "Total Penjualan",
                        data: summary.map((item) => item.total_quantity),
                        backgroundColor: "rgba(75,192,192,0.6)",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `${context.parsed.y.toLocaleString()}`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function (value) {
                            return `${value.toLocaleString()}`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Itemized Summary Table */}
        <div className="card mb-4">
          <div className="card-header">
            <h3 className="card-title">Daily Item Summary</h3>
          </div>
          <div className="table-responsive">
            <table className="table table-hover table-vcenter table-sm card-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Menu</th>
                  <th>Quantity</th>
                  <th>Amount Before Tax</th>
                  <th>Tax Amount</th>
                  <th>Amount After Tax</th>
                </tr>
              </thead>
              <tbody>
                {summary?.length > 0 ? (
                  summary.map((item, index) => (
                    <tr key={item.menu || index}>
                      <td>{index + 1}</td>
                      <td>{item.menu}</td>
                      <td>{item.total_quantity}</td>
                      <td>{formatPrice(Number(item.total_amount_before_tax))}</td>
                      <td>{formatPrice(Number(item.total_tax_amount))}</td>
                      <td>{formatPrice(Number(item.total_amount_after_tax))}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      No summary data found for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="table-container mt-3">
          <h1>Transaction History</h1>
          <div className="search-container mb-3">
            {/* Search input */}
            <SearchInput />

            {/* Active filters info */}
            <div className="ms-3 d-flex align-items-center">
              <small className="text-muted">
                {filterOptions.filters.inserted_at && (
                  <span className="me-2 badge bg-primary text-white">
                    📅 {filterOptions.filters.inserted_at}
                  </span>
                )}
                {filterOptions.filters.location && (
                  <span className="me-2 badge bg-info text-white">
                    📍 {filterOptions.filters.location}
                  </span>
                )}
                {filterOptions.filters.payment_method_id && (
                  <span className="me-2 badge bg-success text-white">
                    💳 Payment Filtered
                  </span>
                )}
                {filterOptions.search && (
                  <span className="me-2 badge bg-warning text-dark">
                    🔍 "{filterOptions.search}"
                  </span>
                )}
              </small>
            </div>

            {/* Export button */}
            <button
              className="btn btn-success ms-2"
              onClick={handleExportExcel}
              title={`Export ${exportDataCount} filtered records to Excel`}
              disabled={exportDataCount === 0}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="icon icon-tabler icon-tabler-file-spreadsheet me-1"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                <path d="M8 11h8v7h-8z" />
                <path d="M8 15h8" />
                <path d="M11 11v7" />
              </svg>
              Export Excel ({exportDataCount} records)
            </button>

            {/* Create button */}
            {/* <button className="btn btn-primary" onClick={() => {}}>
              Create
            </button> */}
          </div>

          {/* Table */}
          <div className="table">
            <table className="table table-hover table-vcenter table-sm">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Code</th>
                  <th>Date</th>
                  <th>cashier</th>
                  <th>Type</th>
                  <th>grand total</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((data, index) => (
                  <React.Fragment key={data.id}>
                    <tr>
                      <td>{index + 1}</td>
                      <td>{data.code}</td>
                      <td>{new Date(data.created_at).toLocaleString()}</td>
                      {/* <td>{data.cashier_session?.cashier?.name || "-"}</td> */}
                      <td>
                        {data.user?.name || "-"}
                        <br />
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          loc: {data.location || "-"}
                        </span>
                      </td>

                      <td>
                        {data.payment_method?.name || "-"}({formatTax(data.payment_method?.tax || 0)}
                        %)
                      </td>
                      <td>{formatPrice(Number(data.grand_total))}</td>
                      <td className="text-end">
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="default"
                            id={`dropdown-${data.id}`}
                          >
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => toggleExpand(data.id)}
                            >
                              {expandedRow === data.id
                                ? "Hide Detail"
                                : "View Detail"}
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => printReceipt(data)}>
                              Reprint Receipt
                            </Dropdown.Item>
                            <PermissionMiddleware
                              allowedPermissions={["transaction_update"]}
                            >
                              <Dropdown.Item
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showCanvasHandler(
                                    "Edit Transaction",
                                    <CategoryProvider>
                                      <MenuProvider>
                                        <EditTransactionPage
                                          initialData={data}
                                          onSuccess={() => {
                                            handleSuccess(true, "Data updated");
                                            refetchTransaction();
                                            refetchReport();
                                          }}
                                        />
                                      </MenuProvider>
                                    </CategoryProvider>,
                                    "fullscreen-canvas"
                                  );
                                }}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                //   onClick={(e) => {
                                //     e.stopPropagation();
                                //     handleDelete(data.id);
                                //   }}
                                onClick={() => handleDelete(data.id)}
                              >
                                Remove
                              </Dropdown.Item>
                            </PermissionMiddleware>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>

                    {/* DETAIL ROW */}
                    {expandedRow === data.id && (
                      <tr>
                        <td colSpan="7">
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Item</th>
                                <th>Qty</th>
                                <th>Price</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.transaction_items.map((item, idx) => (
                                <tr key={item.id || idx}>
                                  <td>{item.item_name || "N/A"}</td>
                                  <td>{item.quantity}</td>
                                  <td>{formatPrice(Number(item.price))}</td>
                                  <td>
                                    {formatPrice(
                                      Number(item.price) * Number(item.quantity)
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td>
                                  <strong>Notes:</strong> {data.notes || "N/A"}
                                </td>
                                <td colSpan="2" className="text-end">
                                  Subtotal :
                                </td>
                                <td>{formatPrice(Number(data.total_price))}</td>
                                {/* notes */}
                              </tr>
                              <tr>
                                <td colSpan="3" className="text-end">
                                  Tax (Produk):
                                </td>
                                <td>
                                  {formatPrice(
                                    data.transaction_items.reduce(
                                      (sum, item) => sum + Number(item.tax_amount || 0),
                                      0
                                    )
                                  )}
                                </td>
                              </tr>
                              {Number(data.payment_method?.tax || 0) > 0 && (
                                <tr>
                                  <td colSpan="3" className="text-end">
                                    Tax/Fee ({data.payment_method?.name || "-"} {formatTax(data.payment_method?.tax)}%):
                                  </td>
                                  <td>
                                    {formatPrice(
                                      (Number(data.total_price) + 
                                        data.transaction_items.reduce(
                                          (sum, item) => sum + Number(item.tax_amount || 0),
                                          0
                                        )
                                      ) * Number(data.payment_method?.tax) / 100
                                    )}
                                  </td>
                                </tr>
                              )}
                              <tr>
                                <td colSpan="3" className="text-end text-muted small">
                                  Total Tax ({data.tax_percent}%):
                                </td>
                                <td className="text-muted small">
                                  {formatPrice(data.tax)}
                                </td>
                              </tr>
                              <tr className="bg-dark text-white">
                                <td colSpan="3" className="text-end">
                                  Grand Total :
                                </td>
                                <td>{formatPrice(data.grand_total)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

              {/* {transactions.length > 0 ? (
                <tbody>
                  {transactions.map((data, index) => (
                    <tr key={data.id}>
                      <td>{index + 1}</td>
                      <td>{data.code}</td>
                      <td>{data.payment_method?.name || "-"}</td>
                      <td>{formatPrice(Number(data.total_price))}</td>
                      <td className="text-end">
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="default"
                            id={`dropdown-${data.id}`}
                          >
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              href="#"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(data.id);
                              }}
                            >
                              Remove
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td colSpan="5" className="text-center">
                      No data available
                    </td>
                  </tr>
                </tbody>
              )} */}
            </table>
          </div>
          {/* <pre>{JSON.stringify(transactions, null, 2)}</pre> */}

          <div className="mt-4 text-center">{Pagination}</div>
        </div>
      </div>
    </LayoutDashboard>
  );
};

export default POS_History;

const SearchInput = () => {
  const [search, setSearch] = useState("");

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      // Implement search logic here
    }
  };

  return (
    <div className="input-icon">
      <input
        type="text"
        className="form-control"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleSearch}
      />
      <span className="input-icon-addon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-search"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <circle cx="10" cy="10" r="7" />
          <line x1="21" y1="21" x2="15" y2="15" />
        </svg>
      </span>
    </div>
  );
};
